import React, { FC, useEffect, useState } from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { useDataProvider } from 'react-admin';
import { DateTime } from 'luxon';
import {
  JobPhaseReceivableAuditDto,
  UserInterface,
} from '@vatos-pas/common';
import { filterByFieldChanges } from 'providers/auditChangeDetection';

import { formatCurrency } from 'utils/currency';

export type ReceivableHistoryVariant = 'supplyPro' | 'billed' | 'invoice';

export type ReceivableHistoryModalProps = {
  open: boolean;
  jobPhaseReceivableId: string | null;
  variant: ReceivableHistoryVariant;
  onClose: () => void;
};

type VariantConfig = {
  title: string;
  fields: Array<keyof JobPhaseReceivableAuditDto>;
  // Detail lines rendered under the "{user} - {timestamp}" line, one per row.
  lines: (row: JobPhaseReceivableAuditDto) => string[];
};

type FetchStatus = 'idle' | 'loading' | 'error' | 'data';

const formatUserName = (user: UserInterface | undefined): string => {
  if (!user) return 'Unknown user';
  const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
  return name.length > 0 ? name : 'Unknown user';
};

const formatTimestamp = (createdAt: Date | string): string => {
  const dt =
    typeof createdAt === 'string'
      ? DateTime.fromISO(createdAt)
      : DateTime.fromJSDate(createdAt);
  if (!dt.isValid) return '';
  return `${dt.toFormat('hh:mm a')} - ${dt.toFormat('MM/dd/yyyy')}`;
};

const trueFalse = (value: boolean): string => (value ? 'True' : 'False');

const VARIANT_CONFIG: Record<ReceivableHistoryVariant, VariantConfig> = {
  supplyPro: {
    title: 'SupplyPro ClickOff Change History',
    fields: ['supplyProIntegrated'],
    lines: row => [`Status: ${trueFalse(row.supplyProIntegrated)}`],
  },
  billed: {
    title: 'Billed Change History',
    fields: ['billed'],
    lines: row => [`Status: ${trueFalse(row.billed)}`],
  },
  invoice: {
    title: 'Invoice Change History',
    fields: ['invoicePOAmount', 'internalInvoiceNumber'],
    lines: row => [
      `Invoice PO Amount: ${formatCurrency(row.invoicePOAmount, '')}`,
      `Internal Invoice #: ${row.internalInvoiceNumber ?? ''}`,
    ],
  },
};

const useStyles = makeStyles(theme => ({
  dialog: {
    '& .MuiDialog-paper': {
      minWidth: 400,
      maxWidth: 500,
    },
  },
  stateMessage: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 160,
  },
  emptyText: {
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
  entry: {
    marginBottom: theme.spacing(3),
  },
  line: {
    lineHeight: 1.4,
    fontSize: 14,
  },
}));

export const ReceivableHistoryModal: FC<ReceivableHistoryModalProps> = ({
  open,
  jobPhaseReceivableId,
  variant,
  onClose,
}) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const config = VARIANT_CONFIG[variant];

  const [status, setStatus] = useState<FetchStatus>('idle');
  const [rows, setRows] = useState<JobPhaseReceivableAuditDto[]>([]);

  useEffect(() => {
    if (!open) return;

    if (!jobPhaseReceivableId) {
      setRows([]);
      setStatus('data');
      return;
    }

    let cancelled = false;
    setStatus('loading');
    setRows([]);

    dataProvider
      .getList<JobPhaseReceivableAuditDto>('job-phase-receivable-audit', {
        filter: { jobPhaseReceivableId },
        sort: { field: 'createdAt', order: 'DESC' },
        pagination: { page: 1, perPage: 1000 },
      })
      .then(response => {
        if (cancelled) return;
        // getList already requests createdAt DESC, the order filterByFieldChanges
        // depends on, so no client-side re-sort is needed.
        const all = response.data ?? [];
        const filtered = filterByFieldChanges(all, config.fields);
        setRows(filtered);
        setStatus('data');
      })
      .catch(() => {
        if (cancelled) return;
        setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [open, jobPhaseReceivableId, dataProvider, variant, config.fields]);

  const renderBody = () => {
    if (status === 'loading' || status === 'idle') {
      return (
        <Box className={classes.stateMessage}>
          <CircularProgress size="1.5rem" />
        </Box>
      );
    }

    if (status === 'error') {
      return (
        <Box className={classes.stateMessage}>
          <Typography className={classes.emptyText}>
            Could not load history.
          </Typography>
        </Box>
      );
    }

    if (rows.length === 0) {
      return (
        <Box className={classes.stateMessage}>
          <Typography className={classes.emptyText}>
            No changes recorded.
          </Typography>
        </Box>
      );
    }

    return (
      <Box>
        {rows.map(row => (
          <Box key={row.id} className={classes.entry}>
            <Typography className={classes.line}>
              {`${formatUserName(row.changedByUser)} - ${formatTimestamp(
                row.createdAt,
              )}`}
            </Typography>
            {config.lines(row).map((line, index) => (
              <Typography key={index} className={classes.line}>
                {line}
              </Typography>
            ))}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={classes.dialog}
      maxWidth="md"
      fullWidth
      aria-labelledby="receivable-history-dialog-title"
    >
      <DialogTitle id="receivable-history-dialog-title">
        {config.title}
      </DialogTitle>
      <DialogContent>{renderBody()}</DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReceivableHistoryModal;
